// TerminologyAdmin — per-tenant terminology overrides.
// Admins pick the words their team already uses (e.g. "Ticket" for a
// hybrid HVAC/PM shop). Changes propagate everywhere via IndustryContext.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useIndustry } from "@/context/IndustryContext";
import { toast } from "sonner";
import { FloppyDisk, ArrowCounterClockwise, Translate } from "@phosphor-icons/react";

const LABEL_META = [
  { key: "work_order", label: "Work Order (long)", hint: "Used on buttons like 'New Work Order' and the sidebar Jobs count." },
  { key: "wo", label: "Work Order (short)", hint: "Short code shown in badges and lists (WO-1234)." },
  { key: "job", label: "Job (informal)", hint: "Used in tech-facing screens: 'Today's Jobs'." },
  { key: "customer", label: "Customer (singular)", hint: "The person who owns the property or requested service." },
  { key: "customer_plural", label: "Customer (plural)", hint: "Plural form for lists and totals." },
  { key: "site", label: "Site / Location", hint: "The physical address where work is done." },
];

export default function TerminologyAdmin() {
  const { reload } = useIndustry();
  const [cfg, setCfg] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/tenant/industry");
    setCfg(r.data);
    setValues({ ...r.data.overrides });
  };
  useEffect(() => { load(); }, []);

  const currentFor = (k) => values[k] ?? cfg?.defaults?.[k] ?? "";
  const isOverridden = (k) => (values[k] || "").trim() && values[k] !== cfg?.defaults?.[k];

  const setVal = (k, v) => setValues(prev => ({ ...prev, [k]: v }));
  const clearVal = (k) => setValues(prev => { const n = { ...prev }; delete n[k]; return n; });

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/tenant/industry/labels", { labels: values });
      toast.success("Terminology saved — refreshing app…");
      if (reload) await reload();
      setTimeout(() => window.location.reload(), 400);
    } catch (e) {
      toast.error("Save failed");
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Reset all terminology back to industry defaults?")) return;
    setValues({});
    await api.post("/tenant/industry/labels", { labels: {} });
    if (reload) await reload();
    setTimeout(() => window.location.reload(), 400);
  };

  if (!cfg) return <div className="p-6 text-slate-500">Loading terminology…</div>;

  return (
    <div className="p-4 md:p-6 max-w-3xl" data-testid="terminology-admin-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-11 w-11 rounded-xl bg-[#0f172a] text-white flex items-center justify-center">
          <Translate weight="bold" size={22}/>
        </div>
        <div>
          <h1 className="font-display font-black text-3xl leading-none">Terminology</h1>
          <div className="text-xs uppercase font-black tracking-widest text-slate-500 mt-1">
            Industry: <span className="text-[#00C4CC]">{cfg.name}</span>
          </div>
        </div>
      </div>
      <p className="text-sm text-slate-600 mb-6 mt-3 max-w-2xl">
        These words appear everywhere in the app — sidebar, buttons, exports, emails.
        Leave blank to use the <b>{cfg.name}</b> default. Overrides apply to your whole team instantly.
      </p>

      <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-200 shadow-sm">
        {LABEL_META.map(({ key, label, hint }) => {
          const def = cfg.defaults?.[key] || "";
          const isOver = isOverridden(key);
          return (
            <div key={key} className="p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
              <div className="md:w-56 flex-shrink-0">
                <div className="font-bold text-sm">{label}</div>
                <div className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">{hint}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={values[key] ?? ""}
                    onChange={(e) => setVal(key, e.target.value)}
                    placeholder={def}
                    data-testid={`term-input-${key}`}
                    className="flex-1 h-10 px-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg text-sm font-medium"
                  />
                  {isOver && (
                    <button onClick={() => clearVal(key)} data-testid={`term-reset-${key}`}
                      title="Reset to industry default"
                      className="h-10 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-black uppercase tracking-widest">
                      Reset
                    </button>
                  )}
                </div>
                <div className="text-[10px] uppercase font-black tracking-widest text-slate-400 mt-1.5">
                  Default: <span className="text-slate-600">{def || "—"}</span>
                  {isOver && <span className="ml-3 text-amber-600">Overridden → {currentFor(key)}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="sticky bottom-4 mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <button onClick={save} disabled={saving} data-testid="term-save-btn"
          className="flex-1 sm:flex-none h-12 px-6 bg-[#00C4CC] hover:bg-[#00a9b1] text-white font-black uppercase tracking-widest text-sm rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg">
          <FloppyDisk weight="bold" size={18}/> {saving ? "Saving…" : "Save Terminology"}
        </button>
        <button onClick={resetAll} data-testid="term-reset-all-btn"
          className="h-12 px-6 border-2 border-slate-300 hover:bg-slate-100 text-slate-700 font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center justify-center gap-2">
          <ArrowCounterClockwise weight="bold" size={16}/> Reset All to Defaults
        </button>
      </div>

      <div className="mt-6 text-[11px] text-slate-500">
        Tip: labels also propagate to worker mobile, exports, and any AI-generated
        emails/SMS your reception sends. Change once, updates everywhere.
      </div>
    </div>
  );
}
